import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useT } from "@/i18n";
import {
  fetchReceipts, fetchReceipt, createReceipt, reconcileReceipt, readReceipt, rematchReceipt, stopReading,
  fetchTransfers, createTransfer, sendTransfer, receiveTransfer, fetchCartonsUnknown,
  type Receipt, type ReceiptLine, type Transfer,
} from "@/services/receiving";
import { fetchBoard } from "@/services/production";
import { ReceiptHead, ReceptionResultView, countText } from "@/components/ReceptionResultView";
import NoteReader from "@/components/NoteReader";
import { ReadOnlyArea, ReadOnlyBanner, useReadOnly } from "@/components/ReadOnly";
import { useFormat } from "@/lib/format";

// His `recErr`: why a reading failed, in the reader's words — never the model's own English.
const errKey = (kind: Receipt["read_error_kind"]) => `note.err.${kind ?? "failed"}`;

export default function ReceivingPage({ warehouseId, onOpenJob }: {
  warehouseId: number | null; onOpenJob?: (eventId: number) => void;
}) {
  const { t } = useT();
  const [open, setOpen] = useState<number | null>(null);
  const qc = useQueryClient();

  // While a document is being read the list watches it, so its result appears without a reload.
  const receipts = useQuery({
    queryKey: ["receipts", warehouseId], queryFn: () => fetchReceipts(warehouseId),
    refetchInterval: (q) => (q.state.data?.receipts.some((r) => r.status === "reading") ? 4000 : false),
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["receipts"] });
    void qc.invalidateQueries({ queryKey: ["receipt"] });
  };
  // A document that went through reading — not a note typed by hand, whatever it was tagged.
  const wasRead = (r: Receipt) => ["reading", "read", "failed", "stopped"].includes(r.status);
  const read = (receipts.data?.receipts ?? []).filter(wasRead);
  const byHand = (receipts.data?.receipts ?? []).filter((r) => !wasRead(r));

  return (
    <div className="space-y-6">
      {/* His banner: Receiving was a Montage tab, so it says what Montage says while Montage is read-only
          too; read-only here only, it says so for Receiving alone (timers can still be started). */}
      <ReadOnlyBanner module="receiving" text={t(useReadOnly("preparation") ? "job.readOnly" : "recv.readOnly")} />
      <Card title={t("note.title")}>
        <p className="px-5 pb-2 pt-1 text-xs text-slate-500">{t("note.hint")}</p>
        {/* His mode line: this server reads every photo and PDF at once (his modeNow). */}
        <p className="px-5 pb-2 text-xs text-slate-500">{t("note.modeNow")}</p>
        <NoteReader warehouseId={warehouseId} onStarted={(r) => { setOpen(r.id); refresh(); }} />
        <h3 className="border-t border-slate-100 px-5 pb-1 pt-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
          {t("note.hist")}
        </h3>
        {receipts.data && read.length === 0 && <p className="px-5 py-3 text-sm text-slate-400">{t("note.none")}</p>}
        <ul className="divide-y divide-slate-100">
          {read.map((r) => (
            <ReadReceiptRow key={r.id} receipt={r} open={open === r.id} onOpenJob={onOpenJob}
              onToggle={() => setOpen(open === r.id ? null : r.id)} onChanged={refresh} />
          ))}
        </ul>
      </Card>

      <Card title={t("recv.title")}>
        <details>
          <summary className="cursor-pointer px-5 py-2.5 text-sm text-slate-700">{t("note.byHand")}</summary>
          <p className="px-5 pb-2 pt-1 text-xs text-slate-500">{t("recv.help")}</p>
          <ReadOnlyArea module="receiving">
            <NewReceipt warehouseId={warehouseId} onCreated={(r) => { setOpen(r.id); refresh(); }} />
          </ReadOnlyArea>
        </details>
        {receipts.data && byHand.length === 0 && <p className="border-t border-slate-100 px-5 py-3 text-sm text-slate-400">{t("recv.none")}</p>}
        <ul className="divide-y divide-slate-100 border-t border-slate-100">
          {byHand.map((r) => (
            <ReceiptRow key={r.id} receipt={r} open={open === r.id}
              onToggle={() => setOpen(open === r.id ? null : r.id)} onChanged={refresh} />
          ))}
        </ul>
      </Card>

      <CartonsUnknownCard />
      <Transfers warehouseId={warehouseId} />
    </div>
  );
}

// His task summary's "sans références", read from the last pass over the supplier orders' PDFs.
function CartonsUnknownCard() {
  const { t } = useT();
  const { dateTime } = useFormat();
  const q = useQuery({ queryKey: ["cartons-unknown"], queryFn: fetchCartonsUnknown, staleTime: 5 * 60_000 });
  const run = q.data?.run;
  if (!run?.outcome) return null;
  const list = run.outcome.without_references;
  return (
    <Card title={t("cartons.unknownTitle")}>
      <p className="px-5 pb-2 pt-1 text-xs text-slate-500">{t("cartons.unknownHint")}</p>
      {list.length === 0 ? <p className="px-5 py-3 text-sm text-slate-400">{t("cartons.allKnown")}</p> : (
        <ul className="divide-y divide-slate-100 border-t border-slate-100">
          {list.map((g) => (
            <li key={g.sku} className="flex flex-wrap items-baseline gap-x-3 px-5 py-2 text-sm">
              <span className="font-medium text-slate-900">{g.title || "—"}</span>
              <span className="font-mono text-xs text-slate-500">{g.sku}</span>
              <span className="text-xs text-slate-600">{t("cartons.nCartons", { n: g.cartons })}</span>
              <span className="font-mono text-xs text-slate-500">{g.order_ids.map((o) => `#${o}`).join(" ")}</span>
            </li>
          ))}
        </ul>
      )}
      <p className="border-t border-slate-100 px-5 py-2 text-xs text-slate-400">{t("cartons.checked", { when: dateTime(run.finished_at ?? run.ran_at) })}</p>
    </Card>
  );
}

// One document read: its header, its pages, and what it means for each client.
function ReadReceiptRow({ receipt, open, onToggle, onChanged, onOpenJob }: {
  receipt: Receipt; open: boolean; onToggle: () => void; onChanged: () => void; onOpenJob?: (eventId: number) => void;
}) {
  const { t, locale } = useT();
  const detail = useQuery({
    queryKey: ["receipt", receipt.id], queryFn: () => fetchReceipt(receipt.id), enabled: open,
    refetchInterval: (q) => (q.state.data?.status === "reading" ? 4000 : false),
  });
  const again = useMutation({ mutationFn: () => readReceipt(receipt.id), onSuccess: onChanged });
  const stop = useMutation({ mutationFn: () => stopReading(receipt.id), onSuccess: onChanged });
  const rematch = useMutation({ mutationFn: () => rematchReceipt(receipt.id), onSuccess: onChanged });
  const ro = useReadOnly("receiving");
  const r = detail.data ?? receipt;
  const s = receipt.summary;

  return (
    <li>
      <button onClick={onToggle} className="flex w-full flex-wrap items-baseline gap-x-3 px-5 py-3 text-left text-sm hover:bg-slate-50">
        {receipt.status === "reading" ? (
          <span className="text-slate-500">⏳ {t("note.reading")}{" "}
            {/* A Stop is a write: not offered to a read-only person. */}
            {!ro && (
              <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); stop.mutate(); }}
                onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); stop.mutate(); } }}
                className="ml-2 rounded px-2 py-0.5 text-xs ring-1 ring-slate-300 hover:bg-white">{t("note.stop")}</span>
            )}
          </span>
        )
          : receipt.status === "failed" ? <span className="text-red-700">⚠ {t("note.failed")} {t(errKey(receipt.read_error_kind))}</span>
          : receipt.status === "stopped" ? <span className="text-slate-500">⏹ {t("note.stopped")}</span>
          : <ReceiptHead receipt={receipt} />}
        {/* His card: the lines read for every document no longer being read, its warehouse (his pending
            card shows it too), its clients. */}
        <span className="ml-auto flex flex-wrap gap-1.5 text-xs">
          {receipt.status !== "reading" && <Tag tone="slate">{countText(t, locale, "note.lines", s.total)}</Tag>}
          {receipt.warehouse_name && <Tag tone="slate">{receipt.warehouse_name}</Tag>}
          {receipt.status !== "reading" && s.clients > 0 && (
            <Tag tone="emerald">{s.client_names?.length ? s.client_names.join(", ") : countText(t, locale, "note.clients", s.clients)}</Tag>
          )}
          {receipt.status === "read" && s.unmatched > 0 && <Tag tone="amber">{countText(t, locale, "recv.unmatched", s.unmatched)}</Tag>}
        </span>
      </button>

      {open && (
        <div className="space-y-3 border-t border-slate-100 bg-slate-50 px-5 py-3">
          {r.media && r.media.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {r.media.map((m) => m.kind === "document" ? (
                <a key={m.id} href={m.url ?? "#"} target="_blank" rel="noopener noreferrer"
                  className="rounded border border-slate-300 bg-white px-2 py-1 text-xs">📄 {m.original_filename || "PDF"}</a>
              ) : (
                <a key={m.id} href={m.url ?? "#"} target="_blank" rel="noopener noreferrer">
                  <img src={m.thumb_url ?? m.url ?? ""} alt="" className="h-20 w-16 rounded border border-slate-200 object-cover" />
                </a>
              ))}
            </div>
          )}
          {r.read_error && r.status === "read" && <p className="text-sm text-amber-800">{t("note.notMatchedRetry")}</p>}
          {r.result && <ReceptionResultView result={r.result} onOpenJob={onOpenJob} />}
          <ReadOnlyArea module="receiving">
            <div className="flex gap-2">
              {(r.status === "failed" || r.status === "stopped") && (
                <button onClick={() => again.mutate()} disabled={again.isPending}
                  className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-40">{t("note.retry")}</button>
              )}
              {/* A check stopped half-way keeps the reading before it: it can be checked again, unpaid. */}
              {(r.status === "read" || (r.status === "stopped" && r.read_at)) && (
                <button onClick={() => rematch.mutate()} disabled={rematch.isPending}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm disabled:opacity-40">{t("note.rematch")}</button>
              )}
            </div>
          </ReadOnlyArea>
        </div>
      )}
    </li>
  );
}

function ReceiptRow({ receipt, open, onToggle, onChanged }: {
  receipt: Receipt; open: boolean; onToggle: () => void; onChanged: () => void;
}) {
  const { t, locale } = useT();
  const detail = useQuery({ queryKey: ["receipt", receipt.id], queryFn: () => fetchReceipt(receipt.id), enabled: open });
  const reconcile = useMutation({ mutationFn: () => reconcileReceipt(receipt.id), onSuccess: onChanged });
  const s = receipt.summary;

  return (
    <li>
      <button onClick={onToggle} className="flex w-full flex-wrap items-baseline gap-x-4 px-5 py-3 text-left hover:bg-slate-50">
        <span className="text-sm font-medium text-slate-800">{receipt.supplier_name || "—"}</span>
        {receipt.document_reference && <span className="text-xs text-slate-500">{receipt.document_reference}</span>}
        {receipt.myotaku_order_id && <span className="text-xs text-slate-400">#{receipt.myotaku_order_id}</span>}
        <span className="text-xs text-slate-400">{receipt.received_on}</span>
        <span className="ml-auto flex gap-1.5 text-xs">
          {s.complete > 0 && <Tag tone="emerald">{countText(t, locale, "recv.complete", s.complete)}</Tag>}
          {s.missing > 0 && <Tag tone="red">{countText(t, locale, "recv.missing", s.missing)}</Tag>}
          {/* "en trop", "extra", "zu viel", "in più": the same for any count. */}
          {s.extra > 0 && <Tag tone="amber">{s.extra} {t("recv.extra")}</Tag>}
          {s.unmatched > 0 && <Tag tone="slate">{countText(t, locale, "recv.unmatched", s.unmatched)}</Tag>}
        </span>
      </button>

      {open && (
        <div className="border-t border-slate-100 bg-slate-50 px-5 py-3">
          <ReadOnlyArea module="receiving">
            <button onClick={() => reconcile.mutate()} disabled={reconcile.isPending || !receipt.myotaku_order_id}
              className="mb-3 rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-40">
              {t("recv.reconcile")}
            </button>
          </ReadOnlyArea>
          {reconcile.error && <p className="mb-2 text-sm text-red-700">{(reconcile.error as Error).message}</p>}
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="py-1 font-medium">{t("recv.sku")}</th>
                <th className="py-1 text-right font-medium">{t("recv.ordered")}</th>
                <th className="py-1 text-right font-medium">{t("recv.received")}</th>
                <th className="py-1 pl-4 font-medium">{t("recv.boxes")}</th>
                <th className="py-1 pl-4 font-medium" />
              </tr>
            </thead>
            <tbody>
              {(detail.data?.lines ?? []).map((l) => <LineRow key={l.id} line={l} />)}
            </tbody>
          </table>
        </div>
      )}
    </li>
  );
}

function LineRow({ line }: { line: ReceiptLine }) {
  const { t } = useT();
  // ⛔ "unmatched" is shown as its own thing, never folded into "extra". A line we cannot place
  // might be a typo, another order on the same pallet, or genuinely unexpected goods — and those
  // need different people.
  const tone = { complete: "emerald", missing: "red", extra: "amber", matched: "emerald", unmatched: "slate" } as const;
  return (
    <tr className="border-t border-slate-200/60">
      <td className="py-1.5">
        <span className="font-medium text-slate-800">{line.sku || "—"}</span>
        {line.label && <span className="ml-2 text-slate-500">{line.label}</span>}
      </td>
      <td className="py-1.5 text-right tabular-nums text-slate-600">{line.ordered_qty ?? "—"}</td>
      <td className="py-1.5 text-right tabular-nums text-slate-800">{line.received_qty ?? "—"}</td>
      <td className="py-1.5 pl-4 text-xs text-slate-500">{line.box_references.join(", ")}</td>
      <td className="py-1.5 pl-4"><Tag tone={tone[line.state]}>{t(`recv.${line.state}`)}</Tag></td>
    </tr>
  );
}

function NewReceipt({ warehouseId, onCreated }: { warehouseId: number | null; onCreated: (r: Receipt) => void }) {
  const { t } = useT();
  const [supplier, setSupplier] = useState("");
  const [ref, setRef] = useState("");
  const [orderId, setOrderId] = useState("");
  // `boxes`: the carton numbers printed on the note for a product shipped in several boxes. They go
  // into the carton library under that product once the note is reconciled.
  const [lines, setLines] = useState<{ sku: string; received_qty: number; boxes: string }[]>([{ sku: "", received_qty: 1, boxes: "" }]);
  const create = useMutation({
    mutationFn: () => createReceipt({
      supplier_name: supplier, document_reference: ref,
      myotaku_order_id: orderId || null, warehouse_id: warehouseId,
      source_kind: "manual",
      lines: lines.filter((l) => l.sku.trim()).map(({ boxes, ...l }) => ({
        ...l, box_references: boxes.split(/[\s,;]+/).map((b) => b.trim()).filter(Boolean),
      })),
    }),
    // Cleared once saved, so a second click cannot file the same note twice.
    onSuccess: (r) => {
      setSupplier(""); setRef(""); setOrderId(""); setLines([{ sku: "", received_qty: 1, boxes: "" }]);
      onCreated(r);
    },
  });

  return (
    <div className="border-t border-slate-100 px-5 py-3">
      <div className="flex flex-wrap gap-2">
        <input value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder={t("recv.supplier")}
          className="w-44 rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
        <input value={ref} onChange={(e) => setRef(e.target.value)} placeholder={t("recv.docRef")}
          className="w-40 rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
        <input value={orderId} onChange={(e) => setOrderId(e.target.value)} placeholder={t("recv.order")} inputMode="numeric"
          className="w-28 rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
      </div>
      <div className="mt-2 space-y-1.5">
        {lines.map((l, i) => (
          <div key={i} className="flex gap-2">
            <input value={l.sku} placeholder={t("recv.sku")}
              onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, sku: e.target.value } : x))}
              className="w-56 rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
            <input type="number" min={0} value={l.received_qty} placeholder={t("recv.received")}
              onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, received_qty: Number(e.target.value) } : x))}
              className="w-24 rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
            <input value={l.boxes} placeholder={t("recv.boxesPh")}
              onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, boxes: e.target.value } : x))}
              className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
          </div>
        ))}
      </div>
      <div className="mt-2 flex gap-2">
        <button onClick={() => setLines([...lines, { sku: "", received_qty: 1, boxes: "" }])}
          className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm">+ {t("recv.addLine")}</button>
        <button onClick={() => create.mutate()} disabled={!supplier.trim() || create.isPending}
          className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-40">
          {t("recv.save")}
        </button>
      </div>
      {create.error && <p className="mt-2 text-sm text-red-700">{(create.error as Error).message}</p>}
    </div>
  );
}

export function Transfers({ warehouseId }: { warehouseId: number | null }) {
  const { t } = useT();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["transfers", warehouseId], queryFn: () => fetchTransfers(warehouseId) });
  const board = useQuery({ queryKey: ["production", warehouseId], queryFn: () => fetchBoard(warehouseId) });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["transfers"] });
    void qc.invalidateQueries({ queryKey: ["production"] });
  };
  const [code, setCode] = useState("");
  const [to, setTo] = useState<number | "">("");
  const [qty, setQty] = useState(1);

  const create = useMutation({
    mutationFn: () => createTransfer({ kit_code: code, from_warehouse_id: warehouseId, to_warehouse_id: to, qty }),
    onSuccess: refresh,
  });
  const send = useMutation({ mutationFn: sendTransfer, onSuccess: refresh });
  const [arriving, setArriving] = useState<number | null>(null);
  const receive = useMutation({ mutationFn: receiveTransfer, onSuccess: () => { setArriving(null); refresh(); } });
  const wh = (id: number) => board.data?.warehouses.find((w) => w.id === id)?.name ?? `#${id}`;

  return (
    <Card title={t("tr.title")}>
      <p className="px-5 pb-2 pt-1 text-xs text-slate-500">{t("tr.inTransitNote")}</p>
      <ReadOnlyArea module="receiving">
        <div className="flex flex-wrap items-end gap-2 border-t border-slate-100 px-5 py-3">
          <select value={code} onChange={(e) => setCode(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm">
            <option value="">{t("prod.kit")}</option>
            {board.data?.stock.map((s) => <option key={s.code} value={s.code}>{s.code}</option>)}
          </select>
          <select value={to} onChange={(e) => setTo(Number(e.target.value))} className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm">
            <option value="">{t("tr.to")}</option>
            {board.data?.warehouses.filter((w) => w.id !== warehouseId).map((w) => <option key={w.id} value={w.id}>{w.name ?? `#${w.id}`}</option>)}
          </select>
          <input type="number" min={1} value={qty} onChange={(e) => setQty(Number(e.target.value))}
            className="w-20 rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
          <button onClick={() => create.mutate()} disabled={!code || !to || warehouseId == null}
            className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-40">
            {t("tr.new")}
          </button>
        </div>
      </ReadOnlyArea>
      {list.data?.transfers.length === 0 && <p className="px-5 py-3 text-sm text-slate-400">{t("tr.none")}</p>}
      <ul className="divide-y divide-slate-100">
        {list.data?.transfers.map((tr: Transfer) => (
          <li key={tr.id} className="flex flex-wrap items-baseline gap-x-3 px-5 py-2.5 text-sm">
            <span className="font-medium text-slate-800">{tr.qty}× {tr.kit_code || tr.article_reference}</span>
            <span className="text-slate-500">{wh(tr.from_myotaku_warehouse_id)} → {wh(tr.to_myotaku_warehouse_id)}</span>
            <Tag tone={tr.status === "received" ? "emerald" : tr.status === "in_transit" ? "amber" : "slate"}>
              {t(tr.status === "received" ? "tr.received" : tr.status === "in_transit" ? "tr.inTransit" : "tr.pending")}
            </Tag>
            {tr.status === "received" && tr.received_qty != null && tr.received_qty < tr.qty && (
              <span className="text-xs text-red-700">
                {t("tr.receivedOf", { n: tr.received_qty, m: tr.qty })}{tr.receive_note ? ` — ${tr.receive_note}` : ""}
              </span>
            )}
            <span className="ml-auto">
              <ReadOnlyArea module="receiving">
                {tr.status === "pending" && (
                  <button onClick={() => send.mutate(tr.id)} className="rounded border border-slate-300 px-2.5 py-1 text-xs">{t("tr.send")}</button>
                )}
                {tr.status === "in_transit" && arriving !== tr.id && (
                  <button onClick={() => { receive.reset(); setArriving(tr.id); }}
                    className="rounded bg-emerald-700 px-2.5 py-1 text-xs font-semibold text-white">{t("tr.receive")}</button>
                )}
              </ReadOnlyArea>
            </span>
            {tr.status === "in_transit" && arriving === tr.id && (
              <Arrival transfer={tr} busy={receive.isPending} error={receive.error as Error | null}
                onCancel={() => setArriving(null)} onConfirm={(receivedQty, note) => receive.mutate({ id: tr.id, receivedQty, note })} />
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

// Ludovic's answer E5.2 (03.11.2026): kits lost or damaged on the way are corrected when the
// destination confirms the arrival — it counts what came, and says why when fewer than were sent.
function Arrival({ transfer, busy, error, onCancel, onConfirm }: {
  transfer: Transfer; busy: boolean; error: Error | null;
  onCancel: () => void; onConfirm: (receivedQty: number, note: string) => void;
}) {
  const { t } = useT();
  const [count, setCount] = useState(String(transfer.qty));
  const [note, setNote] = useState("");
  const n = Number(count);
  const valid = count.trim() !== "" && Number.isInteger(n) && n >= 0 && n <= transfer.qty;
  const short = valid && n < transfer.qty;
  return (
    <div className="mt-2 w-full rounded-lg bg-emerald-50 px-3 py-2">
      <p className="mb-2 text-xs text-slate-600">{t("tr.arrivalHint")}</p>
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1.5 text-xs text-slate-700">
          {t("tr.arrived")}
          <input type="number" min={0} max={transfer.qty} value={count} onChange={(e) => setCount(e.target.value)}
            className="w-20 rounded border border-slate-300 px-2 py-1 text-sm" />
          <span>/ {transfer.qty}</span>
        </label>
        {short && (
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("tr.lossNote")}
            className="min-w-56 flex-1 rounded border border-slate-300 px-2 py-1 text-sm" />
        )}
        <button onClick={() => onConfirm(n, short ? note.trim() : "")} disabled={busy || !valid || (short && !note.trim())}
          className="rounded bg-emerald-700 px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-40">{t("tr.confirmArrival")}</button>
        <button onClick={onCancel} className="rounded border border-slate-300 px-2.5 py-1 text-xs">{t("cancel")}</button>
      </div>
      {error && <p className="mt-1 text-xs text-red-700">{error.message}</p>}
    </div>
  );
}

function Tag({ tone, children }: { tone: "emerald" | "red" | "amber" | "slate"; children: React.ReactNode }) {
  const tones = {
    emerald: "bg-emerald-100 text-emerald-800", red: "bg-red-100 text-red-800",
    amber: "bg-amber-100 text-amber-800", slate: "bg-slate-200 text-slate-700",
  };
  return <span className={`rounded px-1.5 py-0.5 font-medium ${tones[tone]}`}>{children}</span>;
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <h2 className="border-b border-slate-100 bg-slate-50 px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</h2>
      {children}
    </div>
  );
}
